import {Injectable} from "@angular/core";
import {AccessoryCategory, AccessoryCharacteristics} from "../../shared/model/accessory.model";
import {AccessoriesFamily, CharacteristicType, CharacteristicView, Option} from "./characteristics.view";
import {Item, ItemValue, Status} from "../../project/shared/project-model";

@Injectable()
export class CharacteristicsHelperService {

  private defaultCategory = 'T_ACCESSORIES_TITLE';

  /**
   * Get characteristics from model and selected value to create view
   * @param {AccessoryCharacteristics} accessoryCharacteristics the model
   * @param {CharacteristicType} type electrical|accessory
   * @param {ItemValue[]} selectedValues the selected value(s)
   * @returns {CharacteristicView} the view
   */
  public convertCharacteristicToView(accessoryCharacteristics: AccessoryCharacteristics, type: CharacteristicType, selectedValues: ItemValue[]): CharacteristicView {
    const result = new CharacteristicView();
    result.id = accessoryCharacteristics.id;
    result.label = accessoryCharacteristics.name;
    result.tooltip = accessoryCharacteristics.description;
    result.htmlElementType = accessoryCharacteristics.htmlElementType;
    result.required = accessoryCharacteristics.required;
    result.information = accessoryCharacteristics.information;
    result.type = type;
    result.disabled = selectedValues.some(val => val.disabled);
    result.options = accessoryCharacteristics.values.map((val) => {
      const selectItemValues = selectedValues.filter(selVal => selVal.value === val.value);
      return new Option(val.value, val.reference,
        selectedValues.map(selVal => selVal.value).includes(val.value) || (selectedValues.length === 0 && val.id === accessoryCharacteristics.defaultValue),
        selectItemValues.length > 0 ? selectItemValues[0].quantity : 1, val.orderIndex);
    });
    result.category = accessoryCharacteristics.category;
    result.orderIndex = accessoryCharacteristics.orderIndex;
    return result;
  }

  /**
   * Convert CharacteristicView to ItemValue and update current item if it is configured
   * @param currentItemCt
   * @param element
   * @constructor
   */
  public characteristicViewToItemValue(currentItemCt: Item, element: CharacteristicView) {
    if (currentItemCt.status === Status.configured || element.type === CharacteristicType.ACCESSORY) {
      const modifications: ItemValue[] = [];
      element.options.filter(opt => opt.selected).forEach(opt => {
        const modif = new ItemValue();
        modif.id = element.id;
        modif.name = element.label;
        modif.reference = opt.reference;
        modif.value = opt.value;
        modif.chooseValue = true;
        modif.quantity = opt.quantity;
        modif.disabled = false;
        modifications.push(modif);
      });

      // Apply modification to item
      if (element.type === CharacteristicType.ACCESSORY) {
        currentItemCt.accessories = currentItemCt.accessories
          .filter(acc => acc.id !== element.id)
          .concat(modifications);
      } else if (element.type === CharacteristicType.ELECTRICAL) {
        currentItemCt.electricalCharacteristics = currentItemCt.electricalCharacteristics
          .filter(elec => elec.id !== element.id)
          .concat(modifications);
      }
    }
  }

  /**
   * Method to sort accessoriesCharacteristics by category
   */
  public sortAccessoriesByCategory(allAccessories: AccessoriesFamily[], knownCategory: AccessoryCategory[], accessoriesCharacteristicsValues: CharacteristicView[]) {
    const currentlyOpenedCategories: string[] = allAccessories.map((accessory) => { if(accessory.display){return accessory.category}});

    allAccessories = [];

    knownCategory.forEach(category => allAccessories.push(new AccessoriesFamily(category.nameKey, [], currentlyOpenedCategories.includes(category.nameKey))));
    accessoriesCharacteristicsValues.forEach(
      accessory => {
        // Category is just for Accessories range. If category is not defined, we display it in a generic category
        let category = (accessory.category !== null && accessory.category !== undefined) ?
          accessory.category : this.defaultCategory;
        let findCategory = knownCategory.filter(c => category === c.id);
        if (findCategory !== null && findCategory !== undefined && findCategory[0] !== null && findCategory[0].nameKey !== null) {
          category = findCategory[0].nameKey;
        } else {
          category = 'T_ACCESSORIES_CHARACTERISTICS_TITLE';
        }
        this.addAccessoryToAllAccessories(allAccessories, accessory, category, currentlyOpenedCategories.includes(category));
      }
    );
    return allAccessories.filter(value => value.accessories.length > 0);
  }

  /**
   * Method to add an accessory with its category to all accessories
   * @param allAccessories
   * @param accessory
   * @param category
   * @param displayByDefault
   */
  private addAccessoryToAllAccessories(allAccessories: AccessoriesFamily[], accessory: CharacteristicView, category: string, displayByDefault: boolean) {
    const groupAccessory = allAccessories.filter(group => group.category === category);

    // Do not integrate into accessories list, the ones which have less than 2 references.
    if (accessory.options.length > 1) {
      if (groupAccessory.length > 0) {
        groupAccessory[0].accessories.push(accessory);
      } else {
        allAccessories.push(new AccessoriesFamily(category, [accessory], displayByDefault));
      }
    }
  }
}
